import * as _ from 'lodash';
import { useCallback, useEffect } from 'react';
import type { VoidFunctionComponent } from 'react';

import { NOT_FOUND } from '../../../../shared/errors/errorMessage';
import { CopiableText } from '../../../components/generic';
import Flex from '../../../components/generic/Flex';
import List from '../../../components/generic/List';
import Loader from '../../../components/generic/Loader';
import { LoadingBoundary } from '../../../components/generic/LoadingBoundary';
import LicensingSection from '../../../components/projects/dependenciesV2/DependenciesTable/DependencyTableRow/DetailsRow/LicensingSections/LicensingSection';
import { useParams } from '../../../contexts/RouterContext';
import useAsyncRequest from '../../../hooks/useAsyncRequest';
import { get } from '../../../redux/api';
import { releaseDependenciesRoute } from '../../helpers/routes';
import ErrorState from '../ErrorState';
import type { Dependency, ReleaseGroupRelease } from '../../../../shared/publicPortal/types';

import EmptyFossaAnalysisState from './EmptyFossaAnalysisState';
import type { ReleaseGroupParams } from './types';

import styles from './FossaAnalysisReport.module.less';

type Props = {
  release?: ReleaseGroupRelease;
  setTotalItems: (count: number) => void;
  page: number;
  count: number;
};

type Dependencies = Dependency[];

/**
 * Displays the full list of dependencies for a release.
 */
const FossaAnalysisReport: VoidFunctionComponent<Props> = ({ release, setTotalItems, page, count }) => {
  const { slug } = useParams<ReleaseGroupParams>();
  const fetchReport = useCallback(() => {
    if (!release) {
      return Promise.resolve({ data: undefined });
    }

    return get<Dependencies>(releaseDependenciesRoute(slug, release.id));
  }, [slug, release]);
  const value = useAsyncRequest(fetchReport);
  // Set the total count of dependencies for pagination
  useEffect(() => {
    if (value.value?.length) {
      setTotalItems(value.value.length);
    }
    return () => {
      setTotalItems(0);
    };
  }, [setTotalItems, value]);

  return (
    <LoadingBoundary
      asyncValue={value}
      renderValue={({ value: dependencies }) => {
        const start = (page - 1) * count;
        const paginatedDependencies = dependencies.slice(start, start + count);
        return _.isEmpty(paginatedDependencies) ? (
          <div className={styles.error}>
            <EmptyFossaAnalysisState />
          </div>
        ) : (
          <List.Body>
            {paginatedDependencies.map(
              ({ version, locator, licenses = [], declaredLicenses = [], cpes = [], title }) => {
                const cpe = cpes[0];
                return (
                  <List.RowContainer key={locator}>
                    <List.Row hasTightPadding>
                      <Flex direction="column" align="flex-start" gap="8px">
                        <List.RowTitle>
                          {title} {version && <span className={styles.version}>({version})</span>}
                        </List.RowTitle>
                        {cpe && (
                          <List.RowDetails>
                            <CopiableText id={locator} name="CPE" value={cpe} />
                          </List.RowDetails>
                        )}
                        <List.RowDetails>
                          <LicensingSection licenses={licenses} declaredLicenses={declaredLicenses} issues={[]} />
                        </List.RowDetails>
                      </Flex>
                    </List.Row>
                  </List.RowContainer>
                );
              },
            )}
          </List.Body>
        );
      }}
      renderError={({ errors }) => {
        if (errors.includes(NOT_FOUND)) {
          return (
            <div className={styles.error}>
              <EmptyFossaAnalysisState />
            </div>
          );
        }
        return (
          <div className={styles.error}>
            <ErrorState />
          </div>
        );
      }}
      renderLoading={<Loader />}
      renderEmpty={
        <div className={styles.error}>
          <EmptyFossaAnalysisState />
        </div>
      }
    />
  );
};

export default FossaAnalysisReport;
